/*
 * What the shell is handed once, at boot, and what every frame inside it reads: the client model
 * (a Zustand store, so the shell never imports the store module and a test can hand it a store of
 * its own) and the module registry (so a core frame renders a module's contribution without
 * knowing the module exists — architecture.md D21).
 *
 * `useModel(selector)` is the only way a shell component sees state. The selector must return
 * something stable — a primitive, or an object the reducer preserves the identity of (REUSE C5) —
 * because that identity is what decides whether the component re-renders.
 */

import { createContext, useContext, type ReactNode } from "react"
import { useStore, type StoreApi } from "zustand"
import type { ModuleRegistry } from "../modules/registry.ts"
import type { ConversationId, Model } from "../store/model.ts"

export interface ShellServices {
	store: StoreApi<Model>
	registry: ModuleRegistry
}

const ShellContext = createContext<ShellServices | null>(null)

export function ShellServicesProvider({
	services,
	children,
}: {
	services: ShellServices
	children: ReactNode
}) {
	return <ShellContext.Provider value={services}>{children}</ShellContext.Provider>
}

/** The store and the registry. Throws outside the shell, which is a wiring bug, not a state. */
export function useShell(): ShellServices {
	const value = useContext(ShellContext)
	if (!value) throw new Error("useShell outside ShellServicesProvider")
	return value
}

/** One slice of the client model, re-rendering only when that slice changes identity. */
export function useModel<T>(selector: (model: Model) => T): T {
	return useStore(useShell().store, selector)
}

/**
 * The conversation the main pane is showing, or null. The shell does not know how a module routes
 * to a conversation, so the module's route tells it — and the details pane, the announcer and the
 * conversation-scoped shortcuts all read it from here.
 */
const ActiveConversationContext = createContext<{
	id: ConversationId | null
	set: (id: ConversationId | null) => void
}>({ id: null, set: () => undefined })

export const ActiveConversationProvider = ActiveConversationContext.Provider

export function useActiveConversation(): ConversationId | null {
	return useContext(ActiveConversationContext).id
}

/** Called by the route that owns a conversation, with the id it is showing, and null on unmount. */
export function useSetActiveConversation(): (id: ConversationId | null) => void {
	return useContext(ActiveConversationContext).set
}
